import { memo, useCallback } from 'react';

import { BasicTooltip, Flex, Label, Separator, Text, TextField } from '@/components/core';
import { TextArea } from '@/components/core';
import { findField, FormatterValidatorHeader, ValidationFormSettings } from '@/components/ui';
import { useDebounceCallback, useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';

export type RegexValidatorProps = {
  fieldId: string;
  validatorId: string;
};

const IRegexValidator = ({ fieldId, validatorId }: RegexValidatorProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const validator = field?.validators?.find((v) => v.id === validatorId);

  const onSetMatchPattern = useDebounceCallback(
    useCallback((matchPattern: string) => mutate((f) => (f.config.matchPattern = matchPattern || undefined)), [mutate]),
    500
  );

  const onSetMatchFlags = useDebounceCallback(
    useCallback((matchFlags: string) => mutate((f) => (f.config.matchFlags = matchFlags || undefined)), [mutate]),
    500
  );

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader validator={validator} />
      <Separator orientation="horizontal" />

      <Flex col className="gap-sm">
        <TextField onChange={onSetMatchPattern} defaultValue={validator?.config.matchPattern?.toString()}>
          <div className="gap-xs flex">
            <Label isRequired>Match pattern</Label>
            <BasicTooltip>
              <Text slot="description">
                JS RegExp pattern. See{' '}
                <a
                  className="underline"
                  href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions/Cheatsheet"
                  target="_blank"
                >
                  regex cheatsheet
                </a>{' '}
                for more details and examples.
              </Text>
            </BasicTooltip>
          </div>
          <TextArea placeholder="Example: ^(.*) Corp$" />
        </TextField>

        <TextField onChange={onSetMatchFlags} defaultValue={validator?.config.matchFlags?.toString()}>
          <div className="gap-xs flex">
            <Label>Match flags</Label>
            <BasicTooltip>
              <Text slot="description">
                JS RegExp match flags. Multiple flags can be combined by providing a single string containing all flags
                desired, eg. "gs". See{' '}
                <a
                  className="underline"
                  href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions#advanced_searching_with_flags"
                  target="_blank"
                >
                  regex flags documentation
                </a>{' '}
                for more details and examples.
              </Text>
            </BasicTooltip>
          </div>
          <TextArea placeholder="Example: gs" />
        </TextField>
      </Flex>

      <ValidationFormSettings fieldId={fieldId} validatorId={validatorId} />
    </div>
  );
};

export const RegexValidator = memo(IRegexValidator) as typeof IRegexValidator;
